import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Download, FilePlus2, Loader2, Newspaper } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../utils/components/ui/dialog";
import { Button } from "../../utils/components/ui/button";
import { Input } from "../../utils/components/ui/input";
import {
  useGetDeliveryNotesByInvoice,
  type DeliveryNoteResponse,
} from "../../service/invoiseService";

export type DeliveryNoteFormValues = {
  items: {
    invoiceItemId: number;
    quantity: number;
    unit: string;
    conversionToBaseUnit: number;
    baseQuantity: number;
  }[];
};

// Kept for backward-compat with onDownload callback shape
export type DeliveryNoteRecord = {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  deliveryNoteNumber: string;
  createdAt: string;
  items: DeliveryNoteFormValues["items"];
  response?: unknown;
};

type DeliveryNoteModalProps = {
  open: boolean;
  invoice: any | null;
  isDownloading: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateAndDownload: (values: DeliveryNoteFormValues) => void;
  onDownload: (note: DeliveryNoteRecord) => void;
};

const getToday = () => new Date().toISOString().slice(0, 10);

const getInvoiceItems = (invoice: any | null) =>
  Array.isArray(invoice?.invoiceProductResponses)
    ? invoice.invoiceProductResponses
    : [];

function DeliveryNoteModal({
  open,
  invoice,
  isDownloading,
  onOpenChange,
  onCreateAndDownload,
  onDownload,
}: DeliveryNoteModalProps) {
  const invoiceId = invoice?.id ? String(invoice.id) : undefined;

  const {
    data: fetchedNotes = [],
    isLoading: notesLoading,
    refetch: refetchNotes,
  } = useGetDeliveryNotesByInvoice(open ? invoiceId : undefined);

  const invoiceItems = useMemo(() => getInvoiceItems(invoice), [invoice]);

  const nextNoteNumber = useMemo(() => {
    const invoiceNumber = invoice?.invoiceNumber ?? "INV";
    return `DN-${invoiceNumber}-${String(fetchedNotes.length + 1).padStart(2, "0")}`;
  }, [invoice?.invoiceNumber, fetchedNotes.length]);

  const [formValues, setFormValues] = useState<DeliveryNoteFormValues>({
    items: [],
  });

  useEffect(() => {
    if (!open) return;
    setFormValues({
      items: invoiceItems.map((item: any) => ({
        invoiceItemId: Number(item.id),
        quantity: Number(item.qty || 1),
        unit: item.unit || item.product?.unit || "",
        conversionToBaseUnit: Number(item.conversionToBaseUnit || 1),
        baseQuantity:
          Number(item.qty || 1) * Number(item.conversionToBaseUnit || 1),
      })),
    });
  }, [invoiceItems, open]);

  // Refresh the notes list after a new note is created
  useEffect(() => {
    if (open && invoiceId) {
      refetchNotes();
    }
  }, [open, invoiceId]);

  const updateQuantity = (invoiceItemId: number, quantity: number) => {
    setFormValues((current) => ({
      items: current.items.map((item) =>
        item.invoiceItemId === invoiceItemId
          ? {
              ...item,
              quantity: Math.max(0, quantity || 0),
              baseQuantity:
                Math.max(0, quantity || 0) * item.conversionToBaseUnit,
            }
          : item,
      ),
    }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onCreateAndDownload(formValues);
  };

  // Map API response to the DeliveryNoteRecord shape expected by onDownload
  const toRecord = (note: DeliveryNoteResponse): DeliveryNoteRecord => ({
    id: String(note.deliveryNoteId),
    invoiceId: invoiceId ?? "",
    invoiceNumber: note.invoiceNumber,
    deliveryNoteNumber: note.invoiceNumber,
    createdAt: note.createdAt,
    items: [],
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-[980px] overflow-y-auto rounded-[24px] border-none bg-white p-0 shadow-[0_24px_60px_rgba(0,0,0,0.16)] sm:max-w-[980px]">
        <div className="p-6 sm:p-8">
          <DialogHeader className="mb-6">
            <DialogTitle className="flex items-center gap-3 text-[32px] font-semibold tracking-[-0.02em] text-[#1A5EF9]">
              <Newspaper className="size-7" />
              Delivery Note
            </DialogTitle>
            <p className="text-sm text-[#7D8998]">
              {invoice?.invoiceNumber ?? "-"} |{" "}
              {invoice?.customer?.username ?? "Customer not available"}
            </p>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="rounded-[18px] border border-[#E8EEF9]">
              <div className="flex flex-col gap-2 border-b border-[#E8EEF9] bg-[#F5F8FD] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-[#31425E]">
                    Items for Delivery
                  </p>
                  <p className="text-xs text-[#7D8998]">
                    Delivery note number: {nextNoteNumber}
                  </p>
                </div>
                <p className="text-xs font-medium text-[#7D8998]">
                  {getToday()}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="text-left text-sm font-semibold text-[#31425E]">
                      <th className="px-4 py-3">Item</th>
                      <th className="px-4 py-3">Invoice Qty</th>
                      <th className="px-4 py-3">Delivery Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceItems.length ? (
                      invoiceItems.map((item: any) => {
                        const selectedItem = formValues.items.find(
                          (formItem) =>
                            formItem.invoiceItemId === Number(item.id),
                        );
                        return (
                          <tr
                            key={item.id}
                            className="border-t border-[#EEF2FA] text-sm text-[#43536A]"
                          >
                            <td className="px-4 py-4 font-medium text-[#24344F]">
                              {item.product?.productName ?? "-"}
                            </td>
                            <td className="px-4 py-4">{item.qty ?? 0}</td>
                            <td className="px-4 py-4">
                              <Input
                                type="number"
                                min={0}
                                step="any"
                                max={item.qty ?? undefined}
                                value={selectedItem?.quantity ?? 0}
                                onChange={(event) =>
                                  updateQuantity(
                                    Number(item.id),
                                    Number(event.target.value),
                                  )
                                }
                                className="h-10 w-28 rounded-[10px] border-[#E5ECF8] bg-[#F5F8FD]"
                              />
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-4 py-10 text-center text-sm text-[#8D98A7]"
                        >
                          No invoice items are available for this invoice.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                className="h-11 rounded-[12px] bg-[#1A5EF9] px-5 text-white hover:bg-[#0E4FDE]"
                disabled={isDownloading || invoiceItems.length === 0}
              >
                <FilePlus2 className="size-4" />
                Create & Download
              </Button>
            </div>
          </form>

          <div className="mt-8">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#21314A]">
                Created Delivery Notes
              </h3>
              <span className="rounded-[10px] bg-[#F5F8FD] px-3 py-2 text-sm text-[#7D8998]">
                {fetchedNotes.length} notes
              </span>
            </div>
            <div className="overflow-x-auto rounded-[18px] border border-[#E8EEF9]">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-[#F5F8FD] text-left text-sm font-semibold text-[#31425E]">
                    <th className="px-4 py-3">Note Number</th>
                    <th className="px-4 py-3">Created At</th>
                    <th className="px-4 py-3">Qty Delivered</th>
                    <th className="px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {notesLoading ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-10 text-center">
                        <Loader2 className="mx-auto size-5 animate-spin text-[#1A5EF9]" />
                      </td>
                    </tr>
                  ) : fetchedNotes.length ? (
                    fetchedNotes.map((note) => (
                      <tr
                        key={note.deliveryNoteId}
                        className="border-t border-[#EEF2FA] text-sm text-[#43536A]"
                      >
                        <td className="px-4 py-4 font-semibold text-[#24344F]">
                          {note.invoiceNumber}
                        </td>
                        <td className="px-4 py-4">
                          {new Date(note.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4">{note.totalDeliveredQuantity}</td>
                        <td className="px-4 py-4">
                          <div className="flex justify-center">
                            <button
                              type="button"
                              className="rounded-[10px] border border-[#D8E4FB] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9] disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={isDownloading}
                              onClick={() => onDownload(toRecord(note))}
                              title={`Download ${note.invoiceNumber}`}
                              aria-label={`Download ${note.invoiceNumber}`}
                            >
                              <Download className="size-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-10 text-center text-sm text-[#8D98A7]"
                      >
                        No delivery notes have been created for this invoice.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DeliveryNoteModal;
